// Date formatting
export const formatDate = (date: Date | string, locale = 'en-US'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString(locale)
}

export const formatDateTime = (date: Date | string, locale = 'en-US'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleString(locale)
}

export const formatRelativeTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  if (diffInSeconds < 60) return 'just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  return `${Math.floor(diffInSeconds / 86400)} days ago`
}

/**
 * Calculate time ago with i18n support
 * @param dateString - ISO date string
 * @param t - i18n translate function
 * @returns Formatted time ago string
 */
export function getTimeAgo(dateString: string, t: (key: string, values?: Record<string, unknown>) => string): string {
  if (!dateString) return t('recentPosts.timeAgo.justNow')
  
  const now = new Date()
  const postDate = new Date(dateString)
  
  // Check if date is valid
  if (isNaN(postDate.getTime())) return t('recentPosts.timeAgo.justNow')
  
  const diffInMinutes = Math.floor((now.getTime() - postDate.getTime()) / (1000 * 60))
  
  if (diffInMinutes < 1) return t('recentPosts.timeAgo.justNow')
  if (diffInMinutes < 60) {
    const count = diffInMinutes
    return count === 1 
      ? t('recentPosts.timeAgo.minuteAgo', { count })
      : t('recentPosts.timeAgo.minutesAgo', { count })
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return diffInHours === 1 
      ? t('recentPosts.timeAgo.hourAgo', { count: diffInHours })
      : t('recentPosts.timeAgo.hoursAgo', { count: diffInHours })
  }
  
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 30) {
    return diffInDays === 1 
      ? t('recentPosts.timeAgo.dayAgo', { count: diffInDays })
      : t('recentPosts.timeAgo.daysAgo', { count: diffInDays })
  }
  
  const diffInMonths = Math.floor(diffInDays / 30)
  if (diffInMonths < 12) {
    return diffInMonths === 1 
      ? t('recentPosts.timeAgo.monthAgo', { count: diffInMonths })
      : t('recentPosts.timeAgo.monthsAgo', { count: diffInMonths })
  }
  
  const diffInYears = Math.floor(diffInMonths / 12)
  return diffInYears === 1 
    ? t('recentPosts.timeAgo.yearAgo', { count: diffInYears })
    : t('recentPosts.timeAgo.yearsAgo', { count: diffInYears })
}

/**
 * Helper function to get likes text with proper pluralization
 * @param count - Number of likes
 * @param t - i18n translate function
 * @returns Formatted likes text
 */
export function getLikesText(count: number, t: (key: string, values?: Record<string, unknown>) => string): string {
  return count === 1 
    ? t('recentPosts.meta.like', { count })
    : t('recentPosts.meta.likes', { count })
}

/**
 * Helper function to get comments text with proper pluralization
 * @param count - Number of comments
 * @param t - i18n translate function
 * @returns Formatted comments text
 */
export function getCommentsText(count: number, t: (key: string, values?: Record<string, unknown>) => string): string {
  return count === 1 
    ? t('recentPosts.meta.comment', { count })
    : t('recentPosts.meta.comments', { count })
}

export const formatBookingDateTime = (startDateTime: string, endDateTime: string): string => {
  const start = new Date(startDateTime)
  const end = new Date(endDateTime)
  
  // Check if it's today or tomorrow
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  
  const itemDate = new Date(start)
  itemDate.setHours(0, 0, 0, 0)
  
  let dateLabel = ''
  if (itemDate.getTime() === today.getTime()) {
    dateLabel = 'Today'
  } else if (itemDate.getTime() === tomorrow.getTime()) {
    dateLabel = 'Tomorrow'
  } else {
    dateLabel = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  
  const timeRange = `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} - ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
  
  return `${dateLabel} • ${timeRange}`
}

// String formatting
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const truncate = (str: string, length: number, suffix = '...'): string => {
  if (str.length <= length) return str
  return str.substring(0, length) + suffix
}

// Number formatting
export const formatCurrency = (amount: number, currency = 'USD', locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount)
}

export const formatNumber = (num: number, locale = 'en-US'): string => {
  return new Intl.NumberFormat(locale).format(num)
}
